import { ArrowUp, Loader2, Paperclip, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ComposerMode } from '../types';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

type MessageInputActionsProps = {
  mode: ComposerMode;
  onAttach?: () => void;
  onGenerate?: () => void;
  isBusy: boolean;
  isGenerating?: boolean;
  submitLabel?: string;
};

export function MessageInputActions({
  mode,
  onAttach,
  onGenerate,
  isBusy,
  isGenerating = false,
  submitLabel,
}: MessageInputActionsProps) {
  const { t } = useTranslation('mailbox');
  const generateIcon = isGenerating ? Loader2 : Wand2;
  const generateIconClassName = isGenerating ? 'animate-spin' : undefined;
  let submitIcon = <ArrowUp className="size-4" strokeWidth={2.5} />;

  if (isBusy && !isGenerating) {
    submitIcon = <Loader2 className="size-4 animate-spin" strokeWidth={2.5} />;
  }

  const label =
    submitLabel ??
    (mode === 'reply'
      ? t('Reply')
      : mode === 'forward'
        ? t('Forward')
        : t('Send'));

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center">
        <ActionButton
          label={t('Generate AI response')}
          icon={generateIcon}
          iconClassName={generateIconClassName}
          onClick={onGenerate}
          disabled={!onGenerate || isBusy}
        />
        <ActionButton
          label={t('Attach file')}
          icon={Paperclip}
          onClick={onAttach}
          disabled={isBusy}
        />
      </div>

      <Button type="submit" shape="pill" disabled={isBusy}>
        {label}
        {submitIcon}
      </Button>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  iconClassName,
  label,
  onClick,
  disabled = false,
}: {
  icon: typeof Wand2;
  iconClassName?: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="muted"
      size="icon-sm"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <Icon className={cn('size-4', iconClassName)} strokeWidth={2.5} />
    </Button>
  );
}
